import type { BillingErrorBody, BlockedReason } from './types'

import { BillingErrorCodes } from './constants'

const REASONS: ReadonlyArray<BlockedReason> = [
  'free_storage_full',
  'free_ai_exhausted',
  'spend_cap_reached',
  'past_due',
  'canceled',
  'project_limit',
  'seat_limit'
]

const asReason = (value: unknown): BlockedReason | null =>
  typeof value === 'string' && (REASONS as string[]).includes(value) ? (value as BlockedReason) : null

/**
 * Detects a billing 402 from either a fetcher error (status + JSON body) or an SDK
 * error ("402" or "402 <server message>"). Returns `undefined` when the error is not
 * a billing error, otherwise the reason when the response carried one.
 */
export function parseBillingError(error: unknown): { reason: BlockedReason | null } | undefined {
  if (!error || typeof error !== 'object') return undefined

  const err = error as {
    response?: { status?: number }
    data?: Partial<BillingErrorBody>
    message?: unknown
  }

  if (err.response?.status === BillingErrorCodes.PaymentRequired) {
    return { reason: asReason(err.data?.reason) }
  }

  if (typeof err.message === 'string' && /^402(\s|$)/.test(err.message)) {
    const rest = err.message.slice(3).trim()
    const fromJson = (() => {
      try {
        return asReason((JSON.parse(rest) as Partial<BillingErrorBody>)?.reason)
      } catch {
        return null
      }
    })()
    return { reason: fromJson ?? REASONS.find((reason) => rest.includes(reason)) ?? null }
  }

  return undefined
}
